import { useCallback, useEffect, useRef } from "react";

// #476: react-native-web's Modal portals into <body> and hides nothing behind
// it, so a screen reader (and Tab) still reaches the page under the scrim.
// This marks every other child of <body> aria-hidden + inert while a Modal is
// mounted. A per-element counter makes nested modals safe: the first to close
// must not re-enable what the second still covers.
// lib is ES2022 (no DOM): the few members used, typed by hand.
type Element = {
  nodeType: number;
  parentElement: Element | null;
  getAttribute(n: string): string | null;
  setAttribute(n: string, v: string): void;
  removeAttribute(n: string): void;
};
declare const document: { body: (Element & { children: ArrayLike<Element> }) | null } | undefined;
type Saved = { count: number; ariaHidden: string | null; inert: string | null };
const held = new Map<Element, Saved>();

function hold(el: Element) {
  const s = held.get(el);
  if (s) {
    s.count += 1;
    return;
  }
  held.set(el, {
    count: 1,
    ariaHidden: el.getAttribute("aria-hidden"),
    inert: el.getAttribute("inert"),
  });
  el.setAttribute("aria-hidden", "true");
  el.setAttribute("inert", "");
}

function release(el: Element) {
  const s = held.get(el);
  if (!s) return;
  s.count -= 1;
  if (s.count > 0) return;
  held.delete(el);
  if (s.ariaHidden === null) el.removeAttribute("aria-hidden");
  else el.setAttribute("aria-hidden", s.ariaHidden);
  if (s.inert === null) el.removeAttribute("inert");
  else el.setAttribute("inert", s.inert);
}

/** Returns a ref for a node inside the Modal content. */
export function useHideBehindModal(): (node: unknown) => void {
  const undo = useRef<(() => void) | null>(null);
  useEffect(
    () => () => {
      undo.current?.();
      undo.current = null;
    },
    [],
  );
  return useCallback((node: unknown) => {
    undo.current?.();
    undo.current = null;
    const body = typeof document === "undefined" ? null : document.body;
    if (!body || !node || !(node as Element).nodeType) return;
    let top: Element | null = node as Element;
    while (top && top.parentElement !== body) top = top.parentElement;
    if (!top) return;
    const mine = top;
    const others = Array.from(body.children).filter((c) => c !== mine);
    others.forEach(hold);
    undo.current = () => others.forEach(release);
  }, []);
}
